import React, { useState, useEffect } from "react";
import axios from "axios";
import "./AcademicDetails.css";
import API_BASE_URL from "../config";
import { 
  FiBook, 
  FiEdit, 
  FiTrash2, 
  FiPlus, 
  FiX, 
  FiAlertCircle,
  FiUser,
  FiAward,
  FiHome,
  FiCalendar,
  FiPercent,
  FiCheckCircle,
  FiLoader
} from "react-icons/fi";

const AcademicDetails = ({ employee }) => {
  const [academics, setAcademics] = useState([]);
  const [formData, setFormData] = useState({
    level: "SSLC",
    qualification: "",
    university: "",
    yearOfPassing: "",
    grade: "",
  });
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  // Fetch academic records using emp_id
  const fetchAcademics = async () => {
    try {
      setIsLoading(true);
      setError("");
      
      if (!employee || !employee.emp_id) {
        setError("Employee data not available");
        return;
      }
      
      console.log(`🔍 Fetching academics for emp_id: ${employee.emp_id}`);
      
      const res = await axios.get(
        `${API_BASE_URL}/api/academics/by-emp-id/${employee.emp_id}`,
        {
          headers: {
            'Authorization': `Bearer ${sessionStorage.getItem('token')}`
          }
        }
      );
      
      console.log("Academic API response:", res.data);
      
      if (res.data.success) {
        setAcademics(res.data.data);
      } else {
        setAcademics([]);
        setError(res.data.message || "No academic records found");
      }
    } catch (err) {
      console.error("❌ Error fetching academic details:", err);
      setError(err.response?.data?.message || "Failed to fetch academic records");
      setAcademics([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (employee && employee.emp_id) {
      fetchAcademics();
    }
  }, [employee]);

  // Handle form submit (Add/Edit)
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setError("");
      
      const payload = {
        ...formData,
        yearOfPassing: parseInt(formData.yearOfPassing),
        employeeId: employee._id,
        emp_id: employee.emp_id
      };
      
      console.log("Submitting academic data:", payload);
      
      if (editingId) {
        // Update existing record
        await axios.put(
          `${API_BASE_URL}/api/academics/${editingId}`,
          payload,
          {
            headers: {
              'Authorization': `Bearer ${sessionStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            }
          }
        );
        setEditingId(null);
      } else {
        // Add new record
        await axios.post(
          `${API_BASE_URL}/api/academics`,
          payload,
          {
            headers: {
              'Authorization': `Bearer ${sessionStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            }
          }
        );
      }
      
      // Reset form
      setFormData({
        level: "SSLC",
        qualification: "",
        university: "",
        yearOfPassing: "",
        grade: "",
      });
      setShowForm(false);
      
      // Refresh data
      fetchAcademics();
      
    } catch (err) {
      console.error("❌ Error saving academic details:", err);
      setError(err.response?.data?.error || "Failed to save academic record");
    }
  };

  // Edit record
  const handleEdit = (record) => {
    setFormData({
      level: record.level,
      qualification: record.qualification,
      university: record.university,
      yearOfPassing: record.yearOfPassing.toString(),
      grade: record.grade,
    });
    setEditingId(record._id);
    setShowForm(true);
    setError("");
  };

  // Delete record
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this academic record?")) return;
    
    try {
      await axios.delete(
        `${API_BASE_URL}/api/academics/${id}`,
        {
          headers: {
            'Authorization': `Bearer ${sessionStorage.getItem('token')}`
          }
        }
      );
      
      // Refresh data
      fetchAcademics();
      
    } catch (err) {
      console.error("❌ Error deleting academic record:", err);
      setError(err.response?.data?.error || "Failed to delete academic record");
    }
  };

  // Cancel form
  const handleCancel = () => {
    setFormData({
      level: "SSLC",
      qualification: "",
      university: "",
      yearOfPassing: "",
      grade: "",
    });
    setEditingId(null);
    setShowForm(false);
    setError("");
  };

  return (
    <div className="academic-details-container">
      <div className="academic-background-animation">
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#4facfe" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#00f2fe" stopOpacity="0.3" />
            </linearGradient>
          </defs>
          <rect width="100%" height="100%" fill="url(#gradient)" />
          <g stroke="#ffffff" strokeWidth="0.5" strokeOpacity="0.2">
            {[...Array(20)].map((_, i) => (
              <line
                key={i}
                x1={Math.random() * 100 + "%"}
                y1={Math.random() * 100 + "%"}
                x2={Math.random() * 100 + "%"}
                y2={Math.random() * 100 + "%"}
              />
            ))}
          </g>
        </svg>
      </div>

      <div className="academic-header">
        <h2 className="academic-title">
          <span className="academic-icon"><FiBook /></span>
          Academic Qualifications
        </h2>
        <p className="academic-subtitle">
          <FiUser style={{ marginRight: '8px', verticalAlign: 'middle' }} />
          Employee: {employee?.name || "N/A"} (ID: {employee?.emp_id || "N/A"})
        </p>
      </div>

      {error && (
        <div className="error-message">
          <span className="error-icon"><FiAlertCircle /></span>
          {error}
        </div>
      )}

      <button 
        className={`academic-toggle-btn ${showForm ? 'active' : ''}`}
        onClick={() => setShowForm(!showForm)}
      >
        {showForm ? (
          <><span className="icon"><FiX /></span> Close Form</>
        ) : editingId ? (
          <><span className="icon"><FiEdit /></span> Edit Academic</>
        ) : (
          <><span className="icon"><FiPlus /></span> Add New Qualification</>
        )}
      </button>

      {showForm && (
        <div className="academic-form-container slide-in">
          <h3 className="form-title">
            <FiAward style={{ marginRight: '10px', verticalAlign: 'middle' }} />
            {editingId ? "Edit Academic Record" : "Add New Academic Record"}
          </h3>
          <form onSubmit={handleSubmit} className="academic-form">
            <div className="form-group">
              <label htmlFor="level">Education Level *</label>
              <select
                id="level"
                value={formData.level}
                onChange={(e) => setFormData({ ...formData, level: e.target.value })}
                required
              >
                <option value="SSLC">SSLC/10th Grade</option>
                <option value="HSC">HSC/12th Grade</option>
                <option value="UG">Undergraduate (UG)</option>
                <option value="PG">Postgraduate (PG)</option>
                <option value="Diploma">Diploma</option>
                <option value="Doctorate">Doctorate/PhD</option>
                <option value="Other">Other Certification</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="qualification">Qualification/Degree *</label>
              <input
                id="qualification"
                type="text"
                placeholder="e.g., Bachelor of Technology, Master of Science"
                value={formData.qualification}
                onChange={(e) => setFormData({ ...formData, qualification: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="university">University/Institution *</label>
              <input
                id="university"
                type="text"
                placeholder="e.g., Anna University, Harvard University"
                value={formData.university}
                onChange={(e) => setFormData({ ...formData, university: e.target.value })}
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="yearOfPassing">
                  <FiCalendar style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                  Year of Passing *
                </label>
                <input
                  id="yearOfPassing"
                  type="number"
                  min="1950"
                  max={new Date().getFullYear() + 5}
                  placeholder="YYYY"
                  value={formData.yearOfPassing}
                  onChange={(e) => setFormData({ ...formData, yearOfPassing: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="grade">
                  <FiPercent style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                  Grade/Percentage *
                </label>
                <input
                  id="grade"
                  type="text"
                  placeholder="e.g., 8.5 CGPA, 92%"
                  value={formData.grade}
                  onChange={(e) => setFormData({ ...formData, grade: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="form-actions">
              <button type="button" className="cancel-btn" onClick={handleCancel}>
                <FiX style={{ marginRight: '8px' }} />
                Cancel
              </button>
              <button type="submit" className="submit-btn">
                <FiCheckCircle style={{ marginRight: '8px' }} />
                {editingId ? "Update Record" : "Save Record"}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="academic-records">
        <h3 className="records-title">
          <FiHome style={{ marginRight: '10px', verticalAlign: 'middle' }} />
          Academic Records ({academics.length})
        </h3>
        
        {isLoading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p><FiLoader style={{ marginRight: '8px', verticalAlign: 'middle', animation: 'spin 1s linear infinite' }} /> Loading academic records...</p>
          </div>
        ) : academics.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon"><FiBook /></div>
            <h4>No academic records found</h4>
            <p>Add your first qualification to get started</p>
          </div>
        ) : (
          <div className="records-table-container">
            <table className="academic-records-table">
              <thead>
                <tr>
                  <th>Level</th>
                  <th>Qualification</th>
                  <th>University/Institution</th>
                  <th>Year</th>
                  <th>Grade</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {academics.map((record) => (
                  <tr key={record._id} className="fade-in">
                    <td>
                      <span className={`level-badge level-${record.level.toLowerCase()}`}>
                        {record.level}
                      </span>
                    </td>
                    <td>{record.qualification}</td>
                    <td>{record.university}</td>
                    <td>{record.yearOfPassing}</td>
                    <td>
                      <span className="grade-pill">{record.grade}</span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button 
                          className="edit-btn"
                          onClick={() => handleEdit(record)}
                          title="Edit record"
                        >
                          <FiEdit />
                        </button>
                        <button 
                          className="delete-btn"
                          onClick={() => handleDelete(record._id)}
                          title="Delete record"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AcademicDetails;